import { normalizeVelocity } from '../utils';

const noiseBuffers = new Map();

const getNoiseBuffer = (context) => {
  const sampleRate = context.sampleRate;
  if (noiseBuffers.has(sampleRate)) {
    return noiseBuffers.get(sampleRate);
  }
  const buffer = context.createBuffer(1, sampleRate, sampleRate);
  const data = buffer.getChannelData(0);
  for (let index = 0; index < sampleRate; index += 1) {
    data[index] = Math.random() * 2 - 1;
  }
  noiseBuffers.set(sampleRate, buffer);
  return buffer;
};

const connectPartials = ({ context, destination, when, frequency, partials }) => {
  const nodes = [];
  partials.forEach(({ type = 'sine', ratio = 1, detune = 0, gain = 1 }) => {
    const osc = context.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency * ratio, when);
    if (detune) {
      osc.detune.setValueAtTime(detune, when);
    }
    const gainNode = context.createGain();
    gainNode.gain.value = gain;
    osc.connect(gainNode);
    gainNode.connect(destination);
    nodes.push({ osc, gain: gainNode });
  });
  return nodes;
};

const scheduleNodes = (nodes, when, stopTime) => {
  nodes.forEach(({ osc }) => {
    osc.start(when);
    osc.stop(stopTime);
  });
};

// Scale factor so all fixed-second envelopes follow BPM.
// k = 120 / tempo ; at 120 BPM → 1 (no change). At 90 BPM → 1.333 (longer), at 140 BPM → 0.857 (shorter).
const tempoK = (tempo = 120) => 120 / (tempo || 120);

const playTrumpet = ({ context, destination, frequency, velocity, when, tempo = 120 }) => {
  const k = tempoK(tempo);
  const vel = normalizeVelocity(velocity);
  // tiny human jitter so repeated hits don’t stack robotically
  const t0 = Math.max(when, when + (Math.random() - 0.5) * 0.012 * k);

  const amp = context.createGain();
  const peak = vel * 0.95;
  amp.gain.setValueAtTime(0.0001, t0);
  amp.gain.linearRampToValueAtTime(Math.max(0.001, peak), t0 + 0.06 * k);
  amp.gain.linearRampToValueAtTime(Math.max(0.001, peak * 0.72), t0 + 0.32 * k);
  amp.gain.setTargetAtTime(0.0001, t0 + 1.2 * k, 0.4 * k);

  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 0.9;
  // velocity -> brighter tone (more open filter)
  const baseCut = 2.0 + 1.2 * vel;        // 2.0x .. 3.2x fundamental
  const endCut  = 1.4 + 0.4 * vel;        // 1.4x .. 1.8x fundamental
  filter.frequency.setValueAtTime(frequency * baseCut, t0);
  filter.frequency.exponentialRampToValueAtTime(frequency * endCut, t0 + 0.5 * k);

  const breath = context.createBiquadFilter();
  breath.type = 'bandpass';
  breath.frequency.setValueAtTime(frequency * 1.1, t0);
  breath.Q.value = 4;

  // post-gain for subtle tremolo (keeps ADSR intact)
  const post = context.createGain();
  post.gain.setValueAtTime(1, t0);
  amp.connect(filter);
  filter.connect(post);
  post.connect(destination);

  const toneNodes = connectPartials({
    context,
    destination: amp,
    when: t0,
    frequency,
    partials: [
      // velocity biases brighter saws more, sub/square less
      { type: 'sawtooth', ratio: 1, detune: -8, gain: 0.35 + 0.40 * vel },
      { type: 'sawtooth', ratio: 1, detune: 6, gain: 0.22 + 0.28 * vel },
      { type: 'square',   ratio: 0.5,         gain: 0.14 * (1 - 0.6 * vel) },
    ],
  });

  const noiseSource = context.createBufferSource();
  noiseSource.buffer = getNoiseBuffer(context);
  noiseSource.loop = true;
  const noiseGain = context.createGain();
  noiseGain.gain.setValueAtTime(0.0001, t0);
  // a touch more breath with higher velocity
  noiseGain.gain.linearRampToValueAtTime(peak * (0.12 + 0.10 * vel), t0 + 0.08 * k);
  noiseGain.gain.setTargetAtTime(0.0001, t0 + 0.6 * k, 0.25 * k);
  noiseSource.connect(noiseGain);
  noiseGain.connect(breath);
  breath.connect(amp);

  const vibrato = context.createOscillator();
  vibrato.type = 'sine';
  // tiny randomness in vibrato rate helps avoid “MIDI horn”
  vibrato.frequency.setValueAtTime(5.2 + Math.random() * 1.2, t0);
  const vibratoGain = context.createGain();
  vibratoGain.gain.setValueAtTime(0, t0);
  vibratoGain.gain.linearRampToValueAtTime(10 + 4 * vel, t0 + 0.4 * k); // cents
  vibrato.connect(vibratoGain);
  toneNodes.forEach(({ osc }) => vibratoGain.connect(osc.detune));

  // subtle tremolo (post-ADS envelope), depth scales with velocity
  const trem = context.createOscillator();
  trem.type = 'sine';
  trem.frequency.setValueAtTime(5.6 + Math.random() * 0.8, t0);
  const tremAmt = context.createGain();
  tremAmt.gain.setValueAtTime(0.03 + 0.03 * vel, t0); // ~3–6% depth
  trem.connect(tremAmt);
  tremAmt.connect(post.gain);

  const stopTime = t0 + 2.2 * k;
  noiseSource.start(t0);
  noiseSource.stop(t0 + 0.9 * k);
  scheduleNodes(toneNodes, t0, stopTime);
  vibrato.start(t0);
  trem.start(t0);
  vibrato.stop(stopTime);
  trem.stop(stopTime);

  // amp already routed: amp -> filter -> post -> destination

  return stopTime;
};

const playPiano = ({ context, destination, frequency, velocity, when, tempo = 120 }) => {
  const k = tempoK(tempo);  const peak = normalizeVelocity(velocity);
  const amp = context.createGain();
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.linearRampToValueAtTime(Math.max(0.001, peak), when + 0.012 * k);
  amp.gain.exponentialRampToValueAtTime(Math.max(0.001, peak * 0.22), when + 0.28 * k);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 1.5 * k);

  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(5200, when);
  filter.Q.value = 0.9;

  amp.connect(destination);
  filter.connect(amp);

  const partials = connectPartials({
    context,
    destination: filter,
    when,
    frequency,
    partials: [
      { type: 'sine', ratio: 1, gain: 0.8 },
      { type: 'sine', ratio: 2, gain: 0.4 },
      { type: 'sine', ratio: 3, gain: 0.22 },
      { type: 'triangle', ratio: 4, gain: 0.16 },
    ],
  });

  const hammer = context.createBufferSource();
  hammer.buffer = getNoiseBuffer(context);
  hammer.loop = true;
  const hammerGain = context.createGain();
  hammerGain.gain.setValueAtTime(0.0001, when);
  hammerGain.gain.linearRampToValueAtTime(peak * 0.5, when + 0.006 * k);
  hammerGain.gain.exponentialRampToValueAtTime(0.0001, when + 0.12 * k);
  hammer.connect(hammerGain);
  hammerGain.connect(filter);

  const stopTime = when + 1.6 * k;
  scheduleNodes(partials, when, stopTime);
  hammer.start(when);
  hammer.stop(when + 0.16 * k);

  return stopTime;
};

const playMallet = ({ context, destination, frequency, velocity, when, tempo = 120 }) => {
  const k = tempoK(tempo);  const peak = normalizeVelocity(velocity) * 0.95;
  const amp = context.createGain();
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.linearRampToValueAtTime(Math.max(0.001, peak), when + 0.01 * k);
  amp.gain.exponentialRampToValueAtTime(Math.max(0.001, peak * 0.4), when + 0.18 * k);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 1.3 * k);

  const filter = context.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(frequency * 1.6, when);
  filter.Q.value = 5.5;

  amp.connect(destination);
  filter.connect(amp);

  const partials = connectPartials({
    context,
    destination: filter,
    when,
    frequency,
    partials: [
      { type: 'sine', ratio: 1, gain: 0.8 },
      { type: 'sine', ratio: 3, gain: 0.5 },
      { type: 'sine', ratio: 4.2, gain: 0.25 },
      { type: 'sine', ratio: 5.8, gain: 0.22 },
    ],
  });

  const shimmer = context.createOscillator();
  shimmer.type = 'triangle';
  shimmer.frequency.setValueAtTime(frequency * 0.5, when);
  const shimmerGain = context.createGain();
  shimmerGain.gain.setValueAtTime(0.0001, when);
  shimmerGain.gain.linearRampToValueAtTime(peak * 0.15, when + 0.03);
  shimmerGain.gain.exponentialRampToValueAtTime(0.0001, when + 0.5);
  shimmer.connect(shimmerGain);
  shimmerGain.connect(filter);

  const stopTime = when + 1.5 * k;
  scheduleNodes(partials, when, stopTime);
  shimmer.start(when);
  shimmer.stop(when + 0.6);

  return stopTime;
};

const playBell = ({ context, destination, frequency, velocity, when, tempo = 120 }) => {
  const k = tempoK(tempo);  const peak = normalizeVelocity(velocity);
  const amp = context.createGain();
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.linearRampToValueAtTime(Math.max(0.001, peak), when + 0.01 * k);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 3.6 * k);

  const filter = context.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(frequency * 2, when);
  filter.Q.value = 12;

  amp.connect(destination);
  filter.connect(amp);

  const partials = connectPartials({
    context,
    destination: filter,
    when,
    frequency,
    partials: [
      { type: 'sine', ratio: 1, gain: 0.85 },
      { type: 'sine', ratio: 2.7, gain: 0.4 },
      { type: 'sine', ratio: 4.8, gain: 0.25 },
      { type: 'sine', ratio: 6.8, gain: 0.2 },
      { type: 'sine', ratio: 8.9, gain: 0.18 },
    ],
  });

  const shimmer = context.createOscillator();
  shimmer.type = 'sine';
  shimmer.frequency.setValueAtTime(frequency * 1.5, when);
  const shimmerGain = context.createGain();
  shimmerGain.gain.setValueAtTime(0.0001, when);
  shimmerGain.gain.linearRampToValueAtTime(peak * 0.2, when + 0.02 * k);
  shimmerGain.gain.exponentialRampToValueAtTime(0.0001, when + 1.4 * k);
  shimmer.connect(shimmerGain);
  shimmerGain.connect(filter);

  const stopTime = when + 4 * k;
  scheduleNodes(partials, when, stopTime);
  shimmer.start(when);
  shimmer.stop(when + 1.5 * k);

  return stopTime;
};

// --- Flute voice control: keep one active "voice" per destination (i.e., per layer) ---
const fluteLastGain = new WeakMap();  // destination -> last voice gain node
const fluteLastStart = new WeakMap(); // destination -> last note start time (s)

const playFlute = ({ context, destination, frequency, velocity, when, tempo = 120 }) => {
  const k = tempoK(tempo);  const peak = normalizeVelocity(velocity) * 0.9;
  // Monophonic voice gate
  const voice = context.createGain();
  const lastGain = fluteLastGain.get(destination);
  if (lastGain) {
    // Duck previous voice quickly (no clicks)
    lastGain.gain.cancelScheduledValues(when);
    lastGain.gain.setValueAtTime(lastGain.gain.value, when);
    lastGain.gain.setTargetAtTime(0.0001, when, 0.03 * k); // ~30ms at 120 BPM
  }
  // Click-free fade in for current voice
  voice.gain.setValueAtTime(0.0001, when);
  voice.gain.linearRampToValueAtTime(1, when + 0.01 * k);

  const amp = context.createGain();
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.linearRampToValueAtTime(Math.max(0.001, peak), when + 0.12 * k);
  amp.gain.linearRampToValueAtTime(Math.max(0.001, peak * 0.85), when + 0.5 * k);
  amp.gain.setTargetAtTime(0.0001, when + 1.6 * k, 0.45 * k); // a tad shorter tail

  // Route: tone -> filter -> voice -> destination
  voice.connect(destination);
  amp.connect(voice);

  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 0.6;               // a bit smoother
  const startCut = 1.9 + 0.8 * peak;   // slightly darker
  const endCut   = 1.5 + 0.35 * peak;
  filter.frequency.setValueAtTime(frequency * startCut, when);
  filter.frequency.exponentialRampToValueAtTime(frequency * endCut, when + 0.5 * k);

  amp.connect(filter);

  // Tone core with a hint of 3rd partial for color
  const toneNodes = connectPartials({
    context,
    destination: amp,
    when,
    frequency,
    partials: [
      { type: 'sine',     ratio: 1,   gain: 0.82 },
      { type: 'triangle', ratio: 2,   gain: 0.20 },
      { type: 'sine',     ratio: 3,   gain: 0.05 * (0.6 + 0.4 * peak) }, // softer 3rd
    ],
  });

  // Gentle "scoop" into pitch (a few cents) like a real attack
  const detuneStart = (-6 + Math.random() * 4); // -6..-2 cents
  const detuneEndMs = 0.09 * k;
  toneNodes.forEach(({ osc }) => {
    osc.detune.setValueAtTime(detuneStart, when);
    osc.detune.linearRampToValueAtTime(0, when + detuneEndMs);
  });

  // Vibrato: gentle & a bit later
  const vibrato = context.createOscillator();
  vibrato.type = 'sine';
  const vibRate = 5.0 + Math.random() * 0.8; // 5..5.8 Hz
  vibrato.frequency.setValueAtTime(vibRate, when);
  const vibratoGain = context.createGain();
  const vibDelay = 0.26 * k;
  const vibDepthBase = 5 + 3 * peak; // cents
  // If notes are very close together, reduce vibrato depth a bit to avoid "motor" sound
  const lastStart = fluteLastStart.get(destination) ?? -1e9;
  const gap = Math.max(0, when - lastStart);        // seconds
  const rapid = Math.max(0, 1 - gap / (0.18 * k));  // 0..1 (1 = very rapid)
  const vibDepth = vibDepthBase * (1 - 0.25 * rapid);
  vibratoGain.gain.setValueAtTime(0, when);
  vibratoGain.gain.setValueAtTime(0, when + vibDelay);
  vibratoGain.gain.linearRampToValueAtTime(vibDepth, when + vibDelay + 0.25 * k);
  vibrato.connect(vibratoGain);
  toneNodes.forEach(({ osc }) => vibratoGain.connect(osc.detune));

  // (remove tremolo entirely — it sounded like a mechanical echo)

  // Breath noise (velocity scaled), centered a bit above fundamental
  const breathSource = context.createBufferSource();
  breathSource.buffer = getNoiseBuffer(context);
  breathSource.loop = false; // one-shot
  const breathFilter = context.createBiquadFilter();
  breathFilter.type = 'bandpass';
  breathFilter.frequency.setValueAtTime(frequency * 1.2, when);
  breathFilter.Q.value = 5.5;
  const breathGain = context.createGain();
  breathGain.gain.setValueAtTime(0.0001, when);
  // Breath scales down on rapid retriggers to avoid mechanical chuffing
  const breathScale = 0.15 * (1 - 0.6 * rapid);
  breathGain.gain.linearRampToValueAtTime(peak * breathScale, when + 0.16 * k);
  breathGain.gain.exponentialRampToValueAtTime(0.0001, when + 0.55 * k);
  breathSource.connect(breathFilter);
  breathFilter.connect(breathGain);
  breathGain.connect(filter);

  const stopTime = when + 2.1 * k; // slightly shorter overall
  scheduleNodes(toneNodes, when, stopTime);
  vibrato.start(when);
  vibrato.stop(stopTime);
  breathSource.start(when);
  breathSource.stop(stopTime);

  // Remember this voice for next call
  fluteLastGain.set(destination, voice);
  fluteLastStart.set(destination, when);
  return stopTime;
};

export const INSTRUMENTS = [
  {
    id: 'trumpet',
    labelKey: 'instruments.trumpet.label',
    descriptionKey: 'instruments.trumpet.description',
    emoji: '🎺',
    color: '#ff5a63',
    accent: 'rgba(255, 90, 99, 0.18)',
    play: playTrumpet,
  },
  {
    id: 'piano',
    labelKey: 'instruments.piano.label',
    descriptionKey: 'instruments.piano.description',
    emoji: '🎹',
    color: '#6c8bff',
    accent: 'rgba(108, 139, 255, 0.18)',
    play: playPiano,
  },
  {
    id: 'mallet',
    labelKey: 'instruments.mallet.label',
    descriptionKey: 'instruments.mallet.description',
    emoji: '🪘',
    color: '#ffd447',
    accent: 'rgba(255, 212, 71, 0.18)',
    play: playMallet,
  },
  {
    id: 'bell',
    labelKey: 'instruments.bell.label',
    descriptionKey: 'instruments.bell.description',
    emoji: '🛎️',
    color: '#4ad7b7',
    accent: 'rgba(74, 215, 183, 0.18)',
    play: playBell,
  },
  {
    id: 'flute',
    labelKey: 'instruments.flute.label',
    descriptionKey: 'instruments.flute.description',
    emoji: '🎼',
    color: '#b47cff',
    accent: 'rgba(180, 124, 255, 0.18)',
    play: playFlute,
  },
];
